import React, { useCallback, useRef } from 'react';
import { Platform, Pressable, View } from 'react-native';
import { Button, ListItem } from '@rneui/themed';
import BottomSheet, { BottomSheetScrollView } from '@gorhom/bottom-sheet';
import { Portal } from '@gorhom/portal';
import { RANGES } from 'react-native-readium';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useSettingsStore, useUserUUID, READER_FONTS, ReaderTheme, } from "@annunciate/core";
import { DownloadButton } from '@annunciate/ui/dist/components/content/DownloadButton';
import { BookmarkListButton } from '../../bookmark';
import { useTheme } from '../../../hooks';
import { FAIcon, Slider } from '../../elements';
import { ProgressButton } from '../../content';
import { usePlaying, useReaderColors } from '../../../hooks/player';
import { BottomSheetBackdrop } from '../../shared';
const getNameFromTheme = (theme) => {
    if (theme) {
        return theme.charAt(0).toUpperCase() + theme.slice(1);
    }
    return "App";
};
const updatePreferences = useSettingsStore.getState().updateReaderPreferences;
export const ReaderSettings = () => {
    const { colors } = useTheme();
    const { top, bottom } = useSafeAreaInsets();
    const bottomSheetRef = useRef(null);
    const isAuthenticated = !!useUserUUID();
    const { playing } = usePlaying();
    const color = useReaderColors();
    const preferences = useSettingsStore(useCallback((state) => state._readerPreferences, []));
    const onThemeUpdated = useCallback((currentTheme) => {
        if (currentTheme === ReaderTheme.LIGHT) {
            updatePreferences({ theme: ReaderTheme.DARK });
        }
        else if (currentTheme === ReaderTheme.DARK) {
            updatePreferences({ theme: ReaderTheme.SEPIA });
        }
        else if (currentTheme === ReaderTheme.SEPIA) {
            updatePreferences({ theme: undefined });
        }
        else {
            updatePreferences({ theme: ReaderTheme.LIGHT });
        }
    }, []);
    const onFontUpdated = useCallback((currentFont) => {
        const currentIndex = READER_FONTS.findIndex((value) => value === currentFont);
        const newFont = READER_FONTS[currentIndex + 1] || READER_FONTS[0];
        updatePreferences({ fontFamily: newFont });
    }, []);
    const bookmarkRef = useRef(undefined);
    return (React.createElement(React.Fragment, null,
        React.createElement(Pressable, { style: { width: 30, alignItems: 'center' }, onPress: () => bottomSheetRef.current?.expand() },
            React.createElement(FAIcon, { name: "ellipsis-v", size: 30, color: color.backgroundInverted })),
        React.createElement(Portal, { hostName: "modal" },
            React.createElement(BottomSheet, { ref: bottomSheetRef, backdropComponent: BottomSheetBackdrop, index: -1, topInset: top, activeOffsetY: Platform.OS === 'android' ? [-5, 5] : undefined, failOffsetX: Platform.OS === 'android' ? [-5, 5] : undefined, enablePanDownToClose: true, backgroundStyle: { backgroundColor: colors.backgroundPrimary }, handleIndicatorStyle: { backgroundColor: colors.textPrimary }, containerStyle: { zIndex: 3000 } },
                React.createElement(BottomSheetScrollView, null,
                    React.createElement(View, { style: { paddingBottom: bottom } },
                        playing && isAuthenticated && (React.createElement(ListItem, { onPress: () => bookmarkRef.current?.toggle() },
                            React.createElement(ListItem.Content, null,
                                React.createElement(ListItem.Title, null, "Bookmarks")),
                            React.createElement(BookmarkListButton, { item: playing, ref: bookmarkRef, closeParent: () => bottomSheetRef.current?.close() }))),
                        React.createElement(ListItem, null,
                            React.createElement(ListItem.Content, null,
                                React.createElement(ListItem.Title, null, "Theme")),
                            React.createElement(Button, { title: `${getNameFromTheme(preferences.theme)}`, onPress: () => onThemeUpdated(preferences.theme) })),
                        React.createElement(ListItem, null,
                            React.createElement(ListItem.Content, null,
                                React.createElement(ListItem.Title, null, "Font Size"),
                                React.createElement(Slider, { style: { width: '100%' }, minimumValue: RANGES.fontSize[0], maximumValue: RANGES.fontSize[1], step: 0.1, value: preferences.fontSize
                                        ? preferences.fontSize
                                        : RANGES.fontSize[0], onSlidingComplete: (val) => updatePreferences({ fontSize: val }) }))),
                        React.createElement(ListItem, null,
                            React.createElement(ListItem.Content, null,
                                React.createElement(ListItem.Title, null, "Page Margins"),
                                React.createElement(Slider, { style: { width: "100%" }, minimumValue: RANGES.pageMargins[0], maximumValue: RANGES.pageMargins[1], step: 0.1, value: preferences.pageMargins
                                        ? preferences.pageMargins
                                        : RANGES.pageMargins[0], onSlidingComplete: (val) => updatePreferences({ pageMargins: val }) }))),
                        Platform.OS !== "web" ? (React.createElement(ListItem, null,
                            React.createElement(ListItem.Content, null,
                                React.createElement(ListItem.Title, null, "Font")),
                            React.createElement(Button, { title: `${preferences.fontFamily || "Default"}`, onPress: () => onFontUpdated(preferences.fontFamily) }))) : null,
                        playing && (React.createElement(React.Fragment, null,
                            React.createElement(ListItem, null,
                                React.createElement(ListItem.Content, null,
                                    React.createElement(ListItem.Title, null, "Mark Read/Unread")),
                                React.createElement(ProgressButton, { item: playing })),
                            Platform.OS !== "web" && (React.createElement(ListItem, null,
                                React.createElement(ListItem.Content, null,
                                    React.createElement(ListItem.Title, null, "Download/Delete")),
                                React.createElement(DownloadButton, { item: playing })))))))))));
};
//# sourceMappingURL=ReaderSettings.js.map